import { CaretRightOutlined } from "@ant-design/icons";
import { Collapse, Divider, Progress, Space, Tooltip, Typography } from "antd";
import { motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { HomeContext } from "../containers/Home";
import useDragTaskCard from "../hooks/useDragTaskCard";
import useHandleContextMenu from "../hooks/useHandleContextMenu";
import { getDeliverables } from "../services/api-server/deliverables";
import { useProjectStore } from "../stores/projectStore";
import { TDeliverable } from "../types/deliverable";
import { TProjectData } from "../types/project";
import { TTaskData } from "../types/task";
import { excludedMark, statusIcon } from "../utils/statusIcon";
import { TOperationData } from "../types/operation";
import { usePhaseStore } from "../stores/phaseStore";

function TaskCard({
	task,
	operation,
}: {
	task: TTaskData;
	operation?: TOperationData;
}) {
	const navigate = useNavigate();
	const { handleContextMenu } = useHandleContextMenu();
	const { isCardDraggable } = useDragTaskCard();
	const { currentTenantKey }: any = useContext(HomeContext);

	const [deliverableList, setDeliverableList] = useState<TDeliverable[]>([]);
	const [taskUrl, setTaskUrl] = useState<string>("");

	const { project } = useProjectStore();
	const { phase } = usePhaseStore();

	const fetchDeliverables = async (task_id: string) => {
		const deliverables = await getDeliverables(currentTenantKey, task_id);
		setDeliverableList((deliverables as TDeliverable[]) || []);
	};

	const displayExpandList = (list: any) => {
		return list.map((item: any) => {
			let tooltipMessage: any = "";
			if (item.disabled && item.disabled_reason) {
				tooltipMessage = (
					<span>
						This deliverable is excluded from the well programme. You may still
						click to view the details. <br />
						<br /> <b>Comment:</b>
						<br />
						{item?.disabled_reason}
					</span>
				);
			}

			return (
				<Tooltip title={tooltipMessage}>
					<div
						className={
							item.disabled
								? "deliverable-item-disabled"
								: "project-title-clickable"
						}
						onClick={() => {
							navigate(taskUrl, {
								replace: false,
								state: { task_id: task._id, deliverable_id: item._id },
							});
						}}
						onContextMenu={(e: any) => {
							const location = `${window.location.origin}${taskUrl}`;
							handleContextMenu(e, { link: location });
						}}
						style={{
							display: "flex",
							gap: "10px",
							margin: "5px 0px",
							cursor: "pointer",
						}}
					>
						{item.disabled && excludedMark()} {statusIcon(item.status)}{" "}
						{item.deliverable_name}
					</div>
				</Tooltip>
			);
		});
	};

	useEffect(() => {
		fetchDeliverables(task._id);
	}, [task]);

	// Calculate task URL when component mounts or dependencies change
	useEffect(() => {
		const currentPath = window.location.pathname;
		let url: string;

		if (operation) {
			// Extract project path from current URL and construct new path with operation
			const projectPath = currentPath.split("/multi-asset/")[0]; // Get "http://localhost:3000/projects/WE25ELEM004"
			url = `${projectPath}/${operation.operation_code}/${phase?._id}/${task._id}`;
		} else {
			// Append task ID to current URL
			url = `${currentPath}/${task._id}`;
		}

		setTaskUrl(url);
	}, [operation, phase, task._id]);

	const handleDragStart = (
		e: React.DragEvent<HTMLDivElement>,
		task: TTaskData
	) => {
		e.dataTransfer.setData("task", JSON.stringify(task));
	};

	return (
		<motion.div
			layout
			layoutId={task._id}
			className={
				!task.disabled ? "project-card" : "project-card task-card-disabled"
			}
			draggable={
				task.disabled || !isCardDraggable(project as TProjectData, task)
					? false
					: true
			}
			onDragStart={(e: any) => handleDragStart(e, task)}
		>
			<Space
				key={task._id}
				className="project-card-container"
				styles={{ item: { width: "100%" } }}
			>
				<a
					onClick={() => {
						navigate(taskUrl, {
							replace: false,
							state: { task_id: task._id },
						});
					}}
					onContextMenu={(e: any) => {
						const location = `${window.location.origin}${taskUrl}`;
						handleContextMenu(e, { link: location });
					}}
				>
					<Typography.Text
						title=""
						className={"project-title-clickable"}
						style={{
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
						ellipsis={{
							tooltip: <span>{task.task_name}</span>,
						}}
					>
						<span style={{ display: "flex", gap: "5px" }}>
							{task.task_name}
						</span>
					</Typography.Text>
				</a>

				{deliverableList?.length !== 0 && (
					<>
						<Progress
							style={{ width: "100%" }}
							percent={Math.round(task.progress)}
							format={() => {
								const totalCompleted = deliverableList?.filter(
									(item: any) => item.status === "Resolved"
								).length;
								return `${totalCompleted}/${deliverableList?.length}`;
							}}
						/>
						<Collapse
							ghost
							expandIcon={({ isActive }) => (
								<div
									style={{
										display: "flex",
										gap: "5px",
										flex: "auto",
										width: "100%",
									}}
								>
									<CaretRightOutlined rotate={isActive ? 90 : 0} />
									<Divider />
								</div>
							)}
							expandIconPosition="end"
							items={[
								{
									key: "1",
									label: "Deliverables",
									children: <div>{displayExpandList(deliverableList)}</div>,
								},
							]}
						/>
					</>
				)}
			</Space>
		</motion.div>
	);
}

export default TaskCard;
